import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';
import { getDayName, formatTime12Hour } from '../../../utils/schedulingUtils';
import { fetchAllTimeSlots } from '../../../services/timeSlotService';
import {
	fetchRegistrationRequests,
	assignStudentToSlot,
} from '../../../utils/registrationService';
import { convertInquiryToStudents, interestList, fetchInquiryLeads } from '../../../utils/inquiryService';
import { isUnfinishedStep1, step1Line } from '../../../utils/registerStep1';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import AccessNotice from '../../../components/Portal/AccessNotice';
import { isPermissionDenied } from '../../../utils/permissions';

// Fall registration dashboard (Track 1 request-and-assign): open-slot pool +
// incoming family requests + the assign action. Requests are REQUESTS — many
// families may want the same slot; assigning is what takes it off the market
// (server transaction, first wins). Returning-flagged requests sort first
// (priority is Grace's, surfaced by the view — no auto-logic).

// Same discriminator as the server (registration.js) — an open RECURRING slot
// nobody holds. Makeup slots (single-date, isMakeup) and blocked slots are a
// different lifecycle and never show here.
const isOpenRegistrationSlot = (s) =>
	s.status === 'open' && s.isMakeup !== true && s.isRecurring === true && !s.studentId;

const tsToMs = (ts) => ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : 0);
const fmtDate = (ts) => {
	const ms = tsToMs(ts);
	return ms
		? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
		: '—';
};

// "First Last" → { first, last } for prefilling the convert form (same known
// limitation as Inquiries.js: naive split on the first space).
const splitFullName = (full) => {
	const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return { first: '', last: '' };
	return { first: parts[0], last: parts.slice(1).join(' ') };
};

const describeSlot = (slot) =>
	`${getDayName(slot.dayOfWeek)} ${formatTime12Hour(slot.startTime)} · ${slot.location}`;

const STATUS_TONE = { new: 'info', worked: 'warning', placed: 'success', archived: 'neutral' };

const RegistrationTab = () => {
	const { userProfile } = useAuth();
	const isAdminOrOwner = userProfile?.role === 'admin' || userProfile?.owner === true;

	const [slots, setSlots] = useState([]);
	const [requests, setRequests] = useState([]);
	// Families who saved step 1 of /register and never sent times. Not requests
	// — this tab reads registrationRequests, written at step 3, so until
	// 2026-09-07 an unfinished registration could only ever appear under
	// Inquiries, looking exactly like an inquiry.
	const [unfinished, setUnfinished] = useState([]);
	const [students, setStudents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [permDenied, setPermDenied] = useState(false);
	const [error, setError] = useState('');
	const [showDone, setShowDone] = useState(false);
	const [view, setView] = useState('requests'); // 'requests' | 'slots'

	// Assign modal: { request, child, slotId, studentId, studentSearch,
	// convertOpen, guardian, convertChildren, saving, error }
	const [assign, setAssign] = useState(null);

	const loadAll = async () => {
		try {
			const [allSlots, reqs, studentSnap] = await Promise.all([
				fetchAllTimeSlots(),
				fetchRegistrationRequests(),
				getDocs(collection(db, 'students')),
			]);
			setSlots(allSlots);
			setRequests(reqs);
			// Read separately and tolerated: a teacher without the leads read
			// still gets the requests and the pool.
			try {
				const leads = await fetchInquiryLeads();
				setUnfinished((leads || []).filter(isUnfinishedStep1));
			} catch (err) {
				console.warn('Registration: unfinished step-1 leads not readable here', err);
				setUnfinished([]);
			}
			setStudents(
				studentSnap.docs
					.map((d) => ({ id: d.id, ...d.data() }))
					.map((s) => ({
						...s,
						displayName:
							s.fullName || [s.firstName, s.lastName].filter(Boolean).join(' ') || s.id,
					}))
					.sort((a, b) => a.displayName.localeCompare(b.displayName))
			);
			setPermDenied(false);
		} catch (err) {
			console.error('Error loading registration dashboard:', err);
			if (isPermissionDenied(err)) setPermDenied(true);
			else setError('Could not load the registration dashboard. Please refresh.');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadAll();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openSlots = useMemo(() => {
		return slots
			.filter(isOpenRegistrationSlot)
			.sort((a, b) =>
				a.dayOfWeek !== b.dayOfWeek
					? a.dayOfWeek - b.dayOfWeek
					: String(a.startTime).localeCompare(String(b.startTime))
			);
	}, [slots]);
	const openSlotIds = useMemo(() => new Set(openSlots.map((s) => s.id)), [openSlots]);

	// Returning first (admin-side priority), then newest.
	const visibleRequests = useMemo(() => {
		return requests
			.filter((r) => (showDone ? true : r.status === 'new' || r.status === 'worked'))
			.sort((a, b) => {
				if (!!a.returning !== !!b.returning) return a.returning ? -1 : 1;
				return tsToMs(b.createdAt) - tsToMs(a.createdAt);
			});
	}, [requests, showDone]);

	const openSlotsByDay = useMemo(() => {
		const by = {};
		for (const s of openSlots) (by[s.dayOfWeek] = by[s.dayOfWeek] || []).push(s);
		return by;
	}, [openSlots]);

	// ── assign modal ──────────────────────────────────────────────────────────

	const openAssign = (request, child) => {
		const g = splitFullName(request.parent?.name);
		const stillOpen = (child.requestedSlotIds || []).filter((id) => openSlotIds.has(id));
		setAssign({
			request,
			child,
			slotId: stillOpen[0] || '',
			// A change request is pre-linked to the student it came from.
			studentId: child.studentId || '',
			studentSearch: splitFullName(child.childName).first,
			convertOpen: false,
			guardian: {
				firstName: g.first,
				lastName: g.last,
				email: request.parent?.email || '',
				phone: request.parent?.phone || '',
				address: '',
			},
			saving: false,
			converting: false,
			error: '',
		});
	};

	const matchedStudents = useMemo(() => {
		if (!assign) return [];
		const term = assign.studentSearch.trim().toLowerCase();
		const list = term
			? students.filter((s) => s.displayName.toLowerCase().includes(term))
			: students;
		return list.slice(0, 8);
	}, [assign, students]);

	const doConvert = async () => {
		if (!assign?.request?.leadId) return;
		setAssign((a) => ({ ...a, converting: true, error: '' }));
		try {
			const res = await convertInquiryToStudents({
				leadId: assign.request.leadId,
				guardian: assign.guardian,
				students: (assign.request.children || []).map((c) => {
					const n = splitFullName(c.childName);
					return {
						firstName: n.first,
						lastName: n.last,
						age: c.age || '',
						// Primary instrument = first interest; a child doing multiple
						// instruments is extra placements handled manually by an admin.
						instrument: interestList(c.instrument)[0] || 'Voice',
					};
				}),
			});
			// Auto-select this child's freshly created student (children and
			// studentIds are index-aligned in the callable).
			const childIdx = (assign.request.children || []).findIndex(
				(c) => c.childId === assign.child.childId
			);
			const newStudentId = res.studentIds?.[childIdx] || '';
			await loadAll();
			setAssign((a) =>
				a
					? {
							...a,
							converting: false,
							convertOpen: false,
							studentId: newStudentId,
							studentSearch: splitFullName(a.child.childName).first,
					  }
					: a
			);
		} catch (err) {
			console.error('Convert failed:', err);
			setAssign((a) =>
				a
					? {
							...a,
							converting: false,
							error:
								err?.message === 'Inquiry already converted'
									? 'This inquiry was already converted — search for the student above.'
									: err?.message || 'Conversion failed.',
					  }
					: a
			);
		}
	};

	const doAssign = async () => {
		if (!assign?.slotId || !assign?.studentId) {
			setAssign((a) => ({ ...a, error: 'Pick a time slot and a student.' }));
			return;
		}
		setAssign((a) => ({ ...a, saving: true, error: '' }));
		try {
			await assignStudentToSlot({
				slotId: assign.slotId,
				studentId: assign.studentId,
				requestId: assign.request.id,
				childId: assign.child.childId,
			});
			setAssign(null);
			await loadAll();
		} catch (err) {
			console.error('Assign failed:', err);
			// First-wins race loss → refresh the pool so the stale option drops out.
			await loadAll();
			setAssign((a) =>
				a
					? {
							...a,
							saving: false,
							error:
								err?.code === 'functions/failed-precondition' || err?.code === 'failed-precondition'
									? 'That slot was just filled — pick another time.'
									: err?.message || 'Could not assign. Please try again.',
					  }
					: a
			);
		}
	};

	// ── render ────────────────────────────────────────────────────────────────

	if (!isAdminOrOwner) return <AccessNotice what='fall registration' />;
	if (loading) return <div className='ps-spinner' role='status' aria-label='Loading' />;
	if (permDenied) return <AccessNotice what='fall registration' />;

	const newCount = requests.filter((r) => r.status === 'new').length;

	return (
		<div className='registration-tab'>
			<div className='slot-editor__head'>
				<h2 className='ps-card__title'>
					<i className='fa-sharp fa-solid fa-clipboard-list' /> Fall Registration
				</h2>
			</div>

			{error && <div className='ps-alert ps-alert--danger'>{error}</div>}

			<div className='slot-editor__stats'>
				<div className='ps-stat' style={{ borderLeft: '4px solid var(--cyv-teal-600)' }}>
					<p className='ps-stat__num'>{newCount}</p>
					<span className='ps-stat__label'>New Requests</span>
				</div>
				<div className='ps-stat' style={{ borderLeft: '4px solid var(--cyv-teal-500)' }}>
					<p className='ps-stat__num'>{openSlots.length}</p>
					<span className='ps-stat__label'>Open Slots</span>
				</div>
				<div className='ps-stat' style={{ borderLeft: '4px solid var(--cyv-muted)' }}>
					<p className='ps-stat__num'>{requests.filter((r) => r.status === 'placed').length}</p>
					<span className='ps-stat__label'>Placed</span>
				</div>
				<div className='ps-stat' style={{ borderLeft: '4px solid var(--cyv-warning, #c98a17)' }}>
					<p className='ps-stat__num'>{unfinished.length}</p>
					<span className='ps-stat__label'>Started, not finished</span>
				</div>
			</div>

			<div className='ps-toolbar'>
				<div className='ps-tabs registration-subtabs' role='tablist'>
					<button
						type='button'
						role='tab'
						aria-selected={view === 'requests'}
						className={`ps-tab${view === 'requests' ? ' is-active' : ''}`}
						onClick={() => setView('requests')}>
						<i className='fa-sharp fa-solid fa-inbox' /> Requests
					</button>
					<button
						type='button'
						role='tab'
						aria-selected={view === 'slots'}
						className={`ps-tab${view === 'slots' ? ' is-active' : ''}`}
						onClick={() => setView('slots')}>
						<i className='fa-sharp fa-solid fa-calendar-week' /> Open slots
					</button>
				</div>
				{view === 'requests' && (
					<label className='registration-showdone'>
						<input
							type='checkbox'
							checked={showDone}
							onChange={(e) => setShowDone(e.target.checked)}
						/>
						Show placed &amp; archived
					</label>
				)}
			</div>

			{view === 'slots' ? (
				Object.keys(openSlotsByDay).length === 0 ? (
					<div className='ps-alert ps-alert--info'>
						<i className='fa-sharp fa-solid fa-circle-info me-2' />
						No open registration slots. Create the fall pool in the Slot Editor tab.
					</div>
				) : (
					Object.keys(openSlotsByDay)
						.map(Number)
						.sort((a, b) => a - b)
						.map((day) => (
							<div className='registration-day' key={day}>
								<p className='registration-day__name'>{getDayName(day)}</p>
								<div className='registration-day__slots'>
									{openSlotsByDay[day].map((s) => (
										<span className='ps-badge ps-badge--success registration-slot-chip' key={s.id}>
											{formatTime12Hour(s.startTime)} · {s.location}
											{s.instructor ? ` · ${s.instructor}` : ''}
										</span>
									))}
								</div>
							</div>
						))
				)
			) : (
				<>
					{unfinished.length > 0 && (
						<div className='ps-card registration-unfinished'>
							<h3 className='registration-unfinished__title'>
								<i className='fa-sharp fa-solid fa-hourglass-half' /> Started, not finished
								<span className='ps-badge ps-badge--warning'>{unfinished.length}</span>
							</h3>
							<p className='registration-unfinished__hint'>
								These families saved step 1 of /register and never sent their times, so they are not
								requests yet. A nudge goes out once, the day after. Call or email, then convert them
								from Inquiries.
							</p>
							{unfinished.map((l) => (
								<div className='registration-unfinished__row' key={l.id}>
									<div className='registration-unfinished__who'>
										<strong>{l.parent?.name || '—'}</strong>
										<span className='registration-unfinished__contact'>
											{[l.parent?.email, l.parent?.phone].filter(Boolean).join(' · ')}
										</span>
									</div>
									<div className='registration-unfinished__kids'>
										{(l.students || [])
											.map((s) => `${s.name}${s.age ? ` · age ${s.age}` : ''}${s.locationPreference ? ` · ${s.locationPreference}` : ''}`)
											.join(', ') || '—'}
									</div>
									<div className='registration-unfinished__line'>{step1Line(l)}</div>
								</div>
							))}
						</div>
					)}
					{visibleRequests.length === 0 ? (
				<div className='ps-alert ps-alert--info'>
					<i className='fa-sharp fa-solid fa-circle-info me-2' />
					No open registration requests yet. Requests from the public /register form land
					here.
				</div>
			) : (
				visibleRequests.map((r) => (
					<div className='ps-card registration-request' key={r.id}>
						<div className='registration-request__head'>
							<div>
								<p className='registration-request__parent'>
									{/* A lesson-time CHANGE from an existing family (the
									    /change-time form). Assigning releases the old time. */}
									{r.kind === 'slotChange' ? (
										<span className='ps-badge ps-badge--info me-2'>Time change</span>
									) : (
										r.returning && (
											<span className='ps-badge ps-badge--warning me-2'>Returning</span>
										)
									)}
									{r.parent?.name}
								</p>
								<p className='registration-request__meta'>
									{r.parent?.email} · {r.parent?.phone} · {fmtDate(r.createdAt)}
								</p>
							</div>
							<span className={`ps-badge ps-badge--${STATUS_TONE[r.status] || 'neutral'}`}>
								{r.status}
							</span>
						</div>

						{(r.children || []).map((c) => (
							<div className='registration-child' key={c.childId}>
								<div className='registration-child__info'>
									<p className='registration-child__name'>
										{c.childName}
										<span className='registration-child__age'> · age {c.age}</span>
										{interestList(c.instrument).length ? (
											<span className='registration-child__age'> · {interestList(c.instrument).join(', ')}</span>
										) : null}
										{c.returning && r.kind !== 'slotChange' && (
											<span className='ps-badge ps-badge--warning ms-2'>Returning</span>
										)}
									</p>
									{(c.currentSlots || []).length > 0 && (
										<p className='registration-child__age registration-child__current'>
											Current time: {c.currentSlots.map(describeSlot).join(' and ')}
											{c.assignedSlotId ? ' (released on assign)' : ' (releases when you assign)'}
										</p>
									)}
									<div className='registration-child__slots'>
										{(c.requestedSlotIds || []).map((id) => {
											const snap = r.slotsSnapshot?.[id];
											if (!snap) return null;
											const stillOpen = openSlotIds.has(id);
											return (
												<span
													key={id}
													className={`registration-req-slot${stillOpen ? '' : ' is-taken'}`}
													title={stillOpen ? 'Still open' : 'No longer open'}>
													{describeSlot(snap)}
													{!stillOpen && ' (taken)'}
												</span>
											);
										})}
										{c.keepLastYearTime && (
											<span className='registration-req-slot registration-req-slot--hatch'>
												Keep last year's time
											</span>
										)}
										{c.openToAnything && (
											<span className='registration-req-slot registration-req-slot--hatch'>
												Open to anything
											</span>
										)}
									</div>
								</div>
								{c.assignedSlotId ? (
									<span className='ps-badge ps-badge--success registration-child__placed'>
										<i className='fa-sharp fa-solid fa-check me-1' /> Placed
									</span>
								) : (
									<button
										className='btn-cyv-teal registration-assign-btn'
										onClick={() => openAssign(r, c)}>
										Assign
									</button>
								)}
							</div>
						))}

						{r.notes && <p className='registration-request__notes'>“{r.notes}”</p>}
					</div>
				))
			)}
				</>
			)}

			{/* Assign modal */}
			{assign && (
				<div className='ps-modal-overlay' onClick={() => !assign.saving && setAssign(null)}>
					<div className='ps-modal' onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
						<h3 className='ps-modal__title'>
							Assign {assign.child.childName} to a slot
						</h3>

						{assign.error && <div className='ps-alert ps-alert--danger'>{assign.error}</div>}

						<div className='ps-field'>
							<label className='ps-label'>
								Time slot <span className='ps-req'>*</span>
							</label>
							<select
								className='ps-select'
								value={assign.slotId}
								onChange={(e) => setAssign((a) => ({ ...a, slotId: e.target.value }))}>
								<option value=''>Choose a slot…</option>
								<optgroup label='Requested by this family'>
									{(assign.child.requestedSlotIds || [])
										.filter((id) => openSlotIds.has(id))
										.map((id) => {
											const s = openSlots.find((os) => os.id === id);
											return (
												<option key={id} value={id}>
													{s ? describeSlot(s) : id}
												</option>
											);
										})}
								</optgroup>
								<optgroup label='All open slots'>
									{openSlots
										.filter((s) => !(assign.child.requestedSlotIds || []).includes(s.id))
										.map((s) => (
											<option key={s.id} value={s.id}>
												{describeSlot(s)}
											</option>
										))}
								</optgroup>
							</select>
							{(assign.child.requestedSlotIds || []).filter((id) => openSlotIds.has(id))
								.length === 0 && (
								<small className='text-muted'>
									None of this family's requested times are still open — pick from all
									open slots, or reach out to them first.
								</small>
							)}
						</div>

						<div className='ps-field'>
							<label className='ps-label'>
								Student <span className='ps-req'>*</span>
							</label>
							<input
								className='ps-input'
								placeholder='Search students by name…'
								value={assign.studentSearch}
								onChange={(e) => setAssign((a) => ({ ...a, studentSearch: e.target.value }))}
							/>
							<div className='registration-student-picks'>
								{matchedStudents.map((s) => (
									<label className='registration-student-pick' key={s.id}>
										<input
											type='radio'
											name='assign-student'
											checked={assign.studentId === s.id}
											onChange={() => setAssign((a) => ({ ...a, studentId: s.id }))}
										/>
										<span>{s.displayName}</span>
									</label>
								))}
								{matchedStudents.length === 0 && (
									<p className='text-muted registration-nopicks'>No students match.</p>
								)}
							</div>
						</div>

						{/* New family: create roster records via the existing lead-convert
						    pipeline, then assign. No forked student pipeline. */}
						{assign.request.leadId && (
							<div className='registration-convert'>
								{!assign.convertOpen ? (
									<button
										type='button'
										className='btn-cyv-ghost registration-convert-toggle'
										onClick={() => setAssign((a) => ({ ...a, convertOpen: true }))}>
										<i className='fa-sharp fa-solid fa-user-plus me-2' />
										New family? Create student records from this inquiry
									</button>
								) : (
									<div className='registration-convert__form'>
										<p className='registration-convert__hint'>
											Creates the family + one student record per child on this
											request (same pipeline as the Inquiries convert), then selects{' '}
											{assign.child.childName} above.
										</p>
										<div className='slot-editor__form-row'>
											<input
												className='ps-input'
												placeholder='Guardian first name'
												value={assign.guardian.firstName}
												onChange={(e) =>
													setAssign((a) => ({
														...a,
														guardian: { ...a.guardian, firstName: e.target.value },
													}))
												}
											/>
											<input
												className='ps-input'
												placeholder='Guardian last name'
												value={assign.guardian.lastName}
												onChange={(e) =>
													setAssign((a) => ({
														...a,
														guardian: { ...a.guardian, lastName: e.target.value },
													}))
												}
											/>
										</div>
										<button
											type='button'
											className='btn-cyv-teal registration-convert-go'
											onClick={doConvert}
											disabled={assign.converting}>
											{assign.converting ? 'Creating…' : 'Create family + students'}
										</button>
									</div>
								)}
							</div>
						)}

						<div className='ps-modal__footer'>
							<button
								className='btn-cyv-ghost'
								onClick={() => setAssign(null)}
								disabled={assign.saving}>
								Cancel
							</button>
							<button
								className='btn-cyv-teal'
								onClick={doAssign}
								disabled={assign.saving || !assign.slotId || !assign.studentId}>
								{assign.saving ? (
									<>
										<span className='ps-spinner slot-editor__btn-spin' /> Assigning…
									</>
								) : (
									<>
										<i className='fa-sharp fa-solid fa-check' /> Assign to slot
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default RegistrationTab;
